import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { setupCopyV13, setupMedia } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];

// V1.3 Refinement 06 — Find My Setup / guided decision.
// Same approved split composition as V1.2, sharpened for decision assistance:
// numbered question preview (no form controls) → recommended setup → dominant primary CTA.
export const SetupSectionV13 = () => {
  return (
    <section data-testid="setup-section" className="px-6 lg:px-10 py-6 lg:py-10">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="grid grid-cols-2 overflow-hidden rounded-[var(--strollee-radius-hero)] bg-[var(--strollee-dark)]"
      >
        <div className="flex flex-col justify-center p-10 lg:p-16">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FB89E]">
            {setupCopyV13.eyebrow}
          </p>
          <h2 className="mt-4 text-[34px] lg:text-[46px] font-semibold leading-[1.08] tracking-[-0.03em] text-[var(--strollee-bg)]">
            {setupCopyV13.headlineLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h2>
          <p className="mt-5 max-w-[420px] text-base lg:text-lg leading-relaxed text-[#A3A199]">
            {setupCopyV13.support}
          </p>

          {/* Question preview — simple numbered progression, no form controls */}
          <div className="mt-9 max-w-[440px]">
            {setupCopyV13.steps.map((step, i) => (
              <motion.div
                key={step}
                data-testid={`setup-step-${i}`}
                initial={{ opacity: 0, x: -14 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, ease: EASE, delay: 0.3 + i * 0.15 }}
                className="flex items-baseline gap-4 border-t border-white/12 py-3.5"
              >
                <span className="text-[12px] font-semibold tracking-[0.18em] text-white/40">
                  0{i + 1}
                </span>
                <span className="text-[15px] font-medium text-[#EDEAE3]">
                  {step}
                </span>
              </motion.div>
            ))}
            <motion.div
              data-testid="setup-step-result"
              initial={{ opacity: 0, x: -14 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, ease: EASE, delay: 0.3 + setupCopyV13.steps.length * 0.15 }}
              className="mt-2 flex items-center gap-3.5 rounded-lg bg-[#8FB89E]/10 px-4 py-3.5"
            >
              <ArrowRight size={15} strokeWidth={2.2} className="shrink-0 text-[#8FB89E]" />
              <span className="text-[15px] font-semibold text-[#8FB89E]">
                {setupCopyV13.resultLabel}
              </span>
            </motion.div>
          </div>

          {/* CTA hierarchy — primary dominant, secondary quiet text link */}
          <div className="mt-10 flex items-center gap-7">
            <a
              href="#find-my-setup"
              data-testid="setup-primary-cta"
              className="group inline-flex items-center gap-2.5 rounded-full bg-[var(--strollee-surface)] px-8 py-4 text-sm font-semibold tracking-wide text-[var(--strollee-ink)] shadow-[0_10px_32px_rgba(0,0,0,0.35)] transition-all duration-200 hover:-translate-y-[2px] hover:bg-white hover:shadow-[0_14px_40px_rgba(0,0,0,0.45)]"
            >
              {setupCopyV13.primaryCta}
              <ArrowRight
                size={16}
                strokeWidth={2.2}
                className="transition-transform duration-200 group-hover:translate-x-1"
              />
            </a>
            <p className="max-w-[230px] text-[13px] leading-snug text-white/50">
              {setupCopyV13.secondaryHint}{" "}
              <a
                href="#build"
                data-testid="setup-secondary-cta"
                className="group inline-flex items-center gap-1 whitespace-nowrap font-semibold text-white/85 transition-colors duration-200 hover:text-[#8FB89E]"
              >
                {setupCopyV13.secondaryCta}
                <ArrowUpRight
                  size={14}
                  strokeWidth={2.4}
                  className="transition-transform duration-200 group-hover:translate-x-[2px] group-hover:-translate-y-[2px]"
                />
              </a>
            </p>
          </div>
        </div>

        <div className="relative min-h-[520px] lg:min-h-[600px]">
          <img
            src={setupMedia.url}
            alt={setupMedia.alt}
            data-testid="setup-media"
            className="absolute inset-0 h-full w-full object-cover"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F1A15]/45 via-transparent to-transparent" />
        </div>
      </motion.div>
    </section>
  );
};
